<script setup lang="ts">
    import { ConditionalPresentation, type FrontendFieldDefinition } from '@hmmh/storyblok-forms/client';
    import type { ComputedRef } from 'vue';
    import type { FormInputs } from '~/utils/formUtils';
    import FormFieldSelect from './FormFieldSelect.vue';
    import FormErrors from '~/storyblok/Form/FormErrors.vue';
    import { isRequiredField, useFormFieldId } from './form-field-utils';

    interface Props {
        readonly field: FrontendFieldDefinition;
    }

    const props = defineProps<Props>();
    const inputId = useFormFieldId();
    const formInputs = inject<Ref<FormInputs>>('formInputs');
    const activeValues = inject<ComputedRef<Record<string, unknown>>>('formActiveValues');

    const model = computed({
        get: () => String(activeValues?.value[props.field.name] ?? formInputs?.value[props.field.name]?.value ?? ''),
        set: (value: string) => {
            const input = formInputs?.value[props.field.name];

            if (input && !props.field.disabled) input.value = value;
        }
    });

    const errors = computed(() => formInputs?.value[props.field.name]?.errors ?? []);
</script>

<template>
    <fieldset
        v-if="field.presentation === ConditionalPresentation.Radio"
        class="radio-wrapper"
        :disabled="field.disabled"
    >
        <legend>{{ field.label }}<span v-if="isRequiredField(field)"> *</span></legend>
        <div class="input-wrapper">
            <div
                v-for="(option, index) in field.options"
                :key="option.key"
                class="radio-element"
                :class="{ 'is-checked': model === option.key }"
            >
                <input
                    :id="`${inputId}-${index}`"
                    v-model="model"
                    type="radio"
                    :name="field.name"
                    :value="option.key"
                    :required="isRequiredField(field)"
                    :aria-describedby="errors.length ? `error-${inputId}` : undefined"
                    :aria-invalid="errors.length > 0"
                />
                <label :for="`${inputId}-${index}`">{{ option.value }}</label>
            </div>
        </div>
        <FormErrors
            :id="inputId"
            :validation-errors="errors"
        />
    </fieldset>
    <FormFieldSelect
        v-else
        :field="field"
    />
</template>

<style scoped lang="scss">
    @import '~/assets/scss/form/form-group.scss';
    @import '~/assets/scss/form/form-input.scss';

    .radio-element:focus-within {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }

    .radio-element:hover {
        filter: brightness(0.95);
    }

    .radio-element:active {
        filter: brightness(0.9);
    }

    @media (prefers-reduced-motion: reduce) {
        *,
        *::before,
        *::after {
            animation: none !important;
            transition: none !important;
        }
    }
</style>
